import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { TransactionChart } from '../components/charts/TransactionChart';
import { AssetTransactionsChart } from '../components/charts/AssetTransactionsChart';
import { TimeBasedTransactionsChart } from '../components/charts/TimeBasedTransactionsChart';
import { PercentageChart } from '../components/charts/PercentageChart';
import { PaymentPieChart } from '../components/charts/PaymentPieChart';
import { AggregatorPieChart } from '../components/charts/AggregatorPieChart';
import { TransactionSummaryChart } from '../components/charts/TransactionSummaryChart';
import { StatBox } from '../components/stats/StatBox';
import excelIcon from '../assets/excel-icon.svg';
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { gatewayMenuItems } from '@/constants/menuItems';
import transxnIcon from '../logos/transxn.svg';
import successTrxnIcon from '../logos/SuccTrxn.svg';
import overallTrxnIcon from '../logos/OverallTrxn.svg';
import processedAmountIcon from '../logos/ProcessAmnt.svg';
import avgTicketSizeIcon from '../logos/AvgTicket.svg';
import txnLatencyIcon from '../logos/TxnLat.svg';
import conflictedTrxnIcon from '../logos/ConflictTrxn.svg';
import {
  useGetPaymentSuccessRateComparisonQuery,
  useGetTransactionSummaryQuery,
  useExportPaymentSuccessComparisonMutation,
  useGetAmcListQuery,
} from '@/services/dashboardApi';
import { useGetAMCsQuery } from '@/services/amcApi';
import { useGetAggregatorsQuery } from '@/services';
export default function Dashboard() {
  const [selectedGateway, setSelectedGateway] = React.useState('BILLDESK V1');
  const [comparisonPeriod, setComparisonPeriod] = React.useState<'1W' | '1M' | '6M' | '1Y' | 'All'>(
    '1W'
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [exportPaymentSuccessComparison, { isLoading: isDownloading }] =
    useExportPaymentSuccessComparisonMutation();

  // Get user info from Redux store
  const user = useSelector((state: RootState) => state.user);
  const isAmcAdmin = user.role === 'amc_admin';

  // Get AMC ID from URL params or user context
  const amcIdFromUrl = searchParams.get('amcId');
  const effectiveAmcId = isAmcAdmin ? user.amcId : amcIdFromUrl;

  // Debug logging (remove in production)
  // console.log('Dashboard Debug:', { effectiveAmcId });

  // Old AMC API: for AMC admins, fetch all AMCs and derive selectedAmc for header display
  const { data: amcData = [] } = useGetAMCsQuery(undefined, {
    skip: !isAmcAdmin,
  });
  const selectedAmc = isAmcAdmin
    ? amcData.find((amc) => amc.id === user.amcId)
    : undefined;

  // Build API parameters with AMC filtering and selected period
  const apiParams: any = {
    paymentAggregator: selectedGateway,
    type: comparisonPeriod,
  };

  // Add AMC filter if specified
  if (effectiveAmcId) {
    apiParams.amcId = effectiveAmcId;
  }

  const { data: comparisonData } = useGetPaymentSuccessRateComparisonQuery(apiParams);
  const data = comparisonData; // For backward compatibility with existing code

  // Fetch transaction summary data from API with AMC filter
  const transactionSummaryParams: any = {};
  if (effectiveAmcId) {
    transactionSummaryParams.amcId = effectiveAmcId;
  }
  const { data: transactionSummaryData, isLoading: isTransactionSummaryLoading } =
    useGetTransactionSummaryQuery(transactionSummaryParams);

  // Fetch dynamic aggregators based on selected AMC
  const { data: aggregatorsData } = useGetAggregatorsQuery(effectiveAmcId || undefined);

  // Use dynamic aggregators if available, otherwise fall back to hardcoded gateway menu items
  const availableGateways = useMemo(() => {
    if (aggregatorsData?.aggregators && aggregatorsData.aggregators.length > 0) {
      return aggregatorsData.aggregators.map(agg => agg.value);
    }
    return gatewayMenuItems;
  }, [aggregatorsData]);

  // Update selectedGateway when availableGateways loads to prevent empty dropdown
  React.useEffect(() => {
    if (availableGateways.length > 0 && !availableGateways.includes(selectedGateway)) {
      setSelectedGateway(availableGateways[0]);
    }
  }, [availableGateways, selectedGateway]);

  const handleGatewayChange = (event: { target: { value: React.SetStateAction<string> } }) => {
    setSelectedGateway(event.target.value);
  };

  // AMC dropdown state based on effectiveAmcId
  const [selectedAmcId, setSelectedAmcId] = React.useState(effectiveAmcId || '');

  const handleAmcChange = (event: any) => {
    const newAmcId = event.target.value as string;
    setSelectedAmcId(newAmcId);

    // Keep amcId in URL in sync
    const newSearchParams = new URLSearchParams(searchParams);
    if (newAmcId) {
      newSearchParams.set('amcId', newAmcId);
    } else {
      newSearchParams.delete('amcId');
    }
    setSearchParams(newSearchParams);
  };

  // AMC list for dropdown options
  const { data: amcs = [], isLoading: isLoadingAmcs } = useGetAmcListQuery();

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 1,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h1">Dashboard</Typography>
            {selectedAmc && (
              <Chip
                label={`AMC: ${selectedAmc.name}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
          </Box>

          {/* AMC dropdown on the same row, right-aligned */}
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              value={selectedAmcId}
              onChange={handleAmcChange}
              displayEmpty
              disabled={isLoadingAmcs}
            >
              <MenuItem value="">
                <em>All AMCs</em>
              </MenuItem>
              {amcs.map((amc) => (
                <MenuItem key={amc.id} value={amc.id}>
                  {amc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Typography variant="subtitle1">
          {selectedAmc
            ? `Overview of key metrics and insights for ${selectedAmc.name}.`
            : 'Comprehensive overview of key metrics, insights, and actions in one place.'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: '20px', flexDirection: isMobile ? 'column' : 'row' }}>
        <PaymentPieChart
          amcId={effectiveAmcId || undefined}
          onMenuItemSelect={(selected) => {
            console.log('Selected date range:', selected);
            // Handle date selection
          }}
        />
        <AggregatorPieChart
          amcId={effectiveAmcId || undefined}
          onMenuItemSelect={(selected) => {
            console.log('Selected date range:', selected);
            // Handle date selection
          }}
        />
      </Box>
      <Box
        sx={{
          p: 2,
          borderRadius: '10px',
          marginTop: '20px',
          marginBottom: '20px',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h3" sx={{ p: 2, textAlign: isMobile ? 'left' : 'inherit' }}>
              Payment Type Breakdown
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 80, mr: 1 }}>
                <Select
                  value={comparisonPeriod}
                  onChange={(e) =>
                    setComparisonPeriod(e.target.value as '1W' | '1M' | '6M' | '1Y' | 'All')
                  }
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderRadius: '50px',
                    },
                    '& .MuiSelect-select': {
                      padding: '4px 16px',
                      fontSize: '0.75rem',
                    },
                  }}
                >
                  <MenuItem value="1W">1W</MenuItem>
                  <MenuItem value="1M">1M</MenuItem>
                  <MenuItem value="6M">6M</MenuItem>
                  <MenuItem value="1Y">1Y</MenuItem>
                  <MenuItem value="All">All</MenuItem>
                </Select>
              </FormControl>
              <Box
                component="span"
                sx={{
                  color:
                    isDownloading || !comparisonData
                      ? theme.palette.action.disabled
                      : theme.palette.primary.main,
                  fontSize: '0.875rem',
                  mr: 1,
                  lineHeight: 1.5,
                  visibility: isDownloading || !comparisonData ? 'hidden' : 'visible', // Hide text when loading or no data
                }}
              ></Box>
              <Tooltip title={!comparisonData ? 'No data available to export' : 'Download Excel'}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    component="span"
                    sx={{
                      color:
                        isDownloading || !comparisonData
                          ? theme.palette.action.disabled
                          : theme.palette.primary.main,
                      fontSize: '0.875rem',
                      mr: 1,
                      lineHeight: 1.5,
                      visibility: isDownloading ? 'hidden' : 'visible',
                    }}
                  ></Box>
                  <IconButton
                    onClick={async () => {
                      try {
                        const blob = await exportPaymentSuccessComparison({
                          paymentAggregator: selectedGateway,
                          amcId: effectiveAmcId ? String(effectiveAmcId) : undefined,
                          type: comparisonPeriod,
                        }).unwrap();

                        // Create download link
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.style.display = 'none';
                        link.href = url;
                        link.download = 'payment-success-comparison.xlsx';
                        document.body.appendChild(link);
                        link.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(link);
                      } catch (error) {
                        console.error('Download failed:', error);
                        alert('Failed to download file. Please try again.');
                      }
                    }}
                    disabled={isDownloading || !comparisonData}
                    size="small"
                    sx={{
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      },
                      '&.Mui-disabled': {
                        color: theme.palette.action.disabled,
                      },
                    }}
                    aria-label="Export to Excel"
                  >
                    {isDownloading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <img
                        src={excelIcon}
                        alt="Export to Excel"
                        style={{ width: 20, height: 20 }}
                      />
                    )}
                  </IconButton>
                </Box>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small">
              <Select
                value={selectedGateway}
                onChange={handleGatewayChange}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '50px',
                  },
                }}
              >
                {availableGateways.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: `repeat(auto-fit, minmax(280px, 1fr))`,
            },
            gap: '20px',
            marginBottom: '20px',
          }}
        >
          <StatBox
            icon={<img src={transxnIcon} alt="transaction" />}
            title="Transaction Success Rate"
            value={data?.currentWeek.SuccessRate || 0}
            change={parseFloat((data?.changes.SuccessRateChange || 0).toFixed(2))}
            isPositive={(data?.changes.SuccessRateChange || 0) >= 0}
            trendData={[65, 68, 62, 64, 67, 69, 70.72]}
          />
          <StatBox
            icon={<img src={successTrxnIcon} alt="transaction" />}
            title="Success Transactions"
            value={data?.currentWeek.SuccessfulTransactions || 0}
            change={parseFloat((data?.changes.SuccessfulTransactionsChange || 0).toFixed(2))}
            isPositive={(data?.changes.SuccessfulTransactionsChange || 0) >= 0}
            trendData={[80000, 85000, 75000, 82000, 90000, 95000, 98310]}
          />
          <StatBox
            icon={<img src={overallTrxnIcon} alt="transaction" />}
            title="Overall Transactions"
            value={data?.currentWeek.TotalTransactions || 0}
            change={parseFloat((data?.changes.TotalTransactionsChange || 0).toFixed(2))}
            isPositive={(data?.changes.TotalTransactionsChange || 0) >= 0}
            trendData={[120000, 125000, 115000, 128000, 132000, 135000, 139000]}
          />
          <StatBox
            icon={<img src={processedAmountIcon} alt="transaction" />}
            title="Processed Amount"
            value={data?.currentWeek.ProcessedAmount || 0}
            change={parseFloat((data?.changes.ProcessedAmountChange || 0).toFixed(2))}
            isPositive={(data?.changes.ProcessedAmountChange || 0) >= 0}
            trendData={[120000, 125000, 115000, 128000, 132000, 135000, 139000]}
          />
          <StatBox
            icon={<img src={avgTicketSizeIcon} alt="transaction" />}
            title="Average Ticket Size"
            value={data?.currentWeek.AverageTicketSize || 0}
            change={parseFloat((data?.changes.AverageTicketSizeChange || 0).toFixed(2))}
            isPositive={(data?.changes.AverageTicketSizeChange || 0) >= 0}
            trendData={[480, 475, 460, 468, 472, 469, 465.58]}
          />

          <StatBox
            icon={<img src={txnLatencyIcon} alt="transaction" />}
            title="Txn Latency"
            value={'1.8S'}
            change={0.0}
            isPositive={true}
            trendData={[18, 18, 18, 18, 18, 18, 18]}
          />
          <StatBox
            icon={<img src={conflictedTrxnIcon} alt="transaction" />}
            title="Conflicted Transaction Rate"
            value={'0.02%'}
            change={0.04}
            isPositive={false}
            trendData={[0.04, 0.035, 0.03, 0.025, 0.022, 0.021, 0.02]}
          />
          <StatBox
            icon={<img src={conflictedTrxnIcon} alt="transaction" />}
            title="Failed Transactions Rate"
            value={data?.currentWeek.FailedTransactionRate || 0}
            change={parseFloat((data?.changes.FailedTransactionRateChange || 0).toFixed(2))}
            isPositive={(data?.changes.FailedTransactionRateChange || 0) <= 0} // Positive if failure rate decreases
            trendData={[35, 32, 38, 36, 33, 31, 29.28]}
          />
          <StatBox
            icon={<img src={conflictedTrxnIcon} alt="transaction" />}
            title="Failed Transactions"
            value={data?.currentWeek.FailedTransactions || 0}
            change={parseFloat((data?.changes.FailedTransactionsChange || 0).toFixed(2))}
            isPositive={(data?.changes.FailedTransactionsChange || 0) <= 0} // Positive if failures decrease
            trendData={[40000, 40000, 40000, 46000, 42000, 40000, 40690]}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '20px',
          marginBottom: '20px',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <TransactionChart amcId={effectiveAmcId || undefined} />
        <PercentageChart
          mode="gateway"
          amcId={effectiveAmcId || undefined}
          gatewayOptions={availableGateways}
          onMenuItemSelect={(selected) => {
            console.log('Selected gateway:', selected);
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: '20px',
          marginBottom: '20px',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <AssetTransactionsChart amcId={effectiveAmcId || undefined} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: '20px',
          marginBottom: '5px',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <TimeBasedTransactionsChart date={new Date().toISOString().slice(0, 10)} interval={1} />
      </Box>

      {/* Transaction Summary Analysis */}
      <Box
        sx={{
          display: 'flex',
          gap: '20px',
          marginBottom: '5px',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        {isTransactionSummaryLoading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ color: theme.palette.text.secondary }}>
              Loading Transaction Summary...
            </Typography>
          </Box>
        ) : transactionSummaryData ? (
          <TransactionSummaryChart
            data={transactionSummaryData}
            amcId={effectiveAmcId || undefined}
          />
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ color: theme.palette.text.secondary }}>
              No Transaction Summary Data Available
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
